import {RailsApi} from '@/api/rails.api.js'
import {Message} from '@/models/message.model.js'
import {messageDirections} from '@/constants/messages.js'
import {teammateMessageRoles} from '@/constants/teammate.js'

/**
 * My teammate API service
 *
 * Talks to the session-authenticated, current-user-scoped api/v1/my_teammate
 * endpoint. There is no id parameter — the backend resolves the conversation
 * from the current user and organization, so a user can only ever read or post
 * to their own thread.
 *
 * The endpoint speaks the channel-agnostic message shape (role / content). We
 * map it onto the shared Message model so the existing chat components render
 * it unchanged: an assistant turn is outgoing (sent by Arist), a user turn is
 * incoming (sent by the learner).
 */
export class MyTeammateApi extends RailsApi {

  //Props
  basePath = '/my_teammate'
  itemKey = 'message'

  /**
   * Get the current user's teammate thread (messages + conversation metadata)
   */
  async getThread(params, options) {
    const json = await this.get('', params, options)
    return {
      conversation: json.conversation,
      //Filter out nulls: toMessage guards against falsy payload elements, and a
      //null in the array would crash message reconciliation downstream
      messages: (json.messages || [])
        .map(message => this.toMessage(message))
        .filter(Boolean),
    }
  }

  /**
   * Post a message from the user
   */
  async createMessage(body) {
    const {itemKey} = this
    const json = await this.post('/messages', {[itemKey]: {body}})
    return this.toMessage(json.message)
  }

  /**
   * Convert a teammate message payload to a Message model. The teammate API
   * speaks roles (user / assistant); the chat components speak directions
   * (incoming = from the learner, outgoing = from Arist).
   */
  toMessage(data) {
    if (!data) {
      return null
    }
    return Message.createFrom({
      id: data.id,
      body: data.content,
      sentAt: data.createdAt,
      direction: data.role === teammateMessageRoles.USER ?
        messageDirections.INCOMING :
        messageDirections.OUTGOING,
    })
  }
}

//Export singleton instance
export const myTeammateApi = new MyTeammateApi()
